// Copyright (c) 2026 杨翔宇（武汉文理学院）
// 物理智能教学智能体平台 (Physics Intelligent Agent)
// 保留所有权利 (All Rights Reserved)。未经著作权人书面许可，
// 不得以任何形式复制、传播、修改或用于商业用途。

import { ApiBase } from './apiBase';

export interface MemoryItem {
  id: string;
  content: string;
  source: 'user' | 'auto';
  category: string;
  pinned: boolean;
  created_at: number;
  updated_at: number;
}

export interface MemoryListResponse {
  items: MemoryItem[];
  total: number;
}

export interface TeacherTemplateSnapshot {
  content_type: string;
  query: string;
  preferences: Record<string, string>;
  content_preview: string;
  timestamp: number;
  template_state?: 'complete' | 'fallback';
  template_key?: string;
}

export interface RuntimeSnapshotResponse {
  user_memories: {
    items: MemoryItem[];
    total: number;
    context: string;
    has_memories: boolean;
  };
  teacher: {
    preferences: Record<string, unknown>;
    defaults: Record<string, unknown>;
    templates: TeacherTemplateSnapshot[];
    history: Array<Record<string, unknown>>;
  };
  student: {
    profile: Record<string, unknown>;
    weak_topics: Array<Record<string, unknown>>;
    recent_history: Array<Record<string, unknown>>;
    error_stats: Record<string, unknown>;
  };
  checkpoint: null | {
    thread_id: string;
    checkpoint_id: string;
    namespace: string;
    metadata: Record<string, unknown>;
    state: Record<string, unknown>;
  };
}

export class ApiMemories extends ApiBase {
  async listMemories(params?: { source?: 'user' | 'auto'; category?: string }) {
    return this.client.get<MemoryListResponse>('/v1/memories', { params });
  }

  async createMemory(data: { content: string; category?: string; pinned?: boolean }) {
    return this.client.post<MemoryItem>('/v1/memories', data);
  }

  async updateMemory(id: string, data: { content?: string; category?: string; pinned?: boolean }) {
    return this.client.put<MemoryItem>(`/v1/memories/${id}`, data);
  }

  async deleteMemory(id: string) {
    return this.client.delete(`/v1/memories/${id}`);
  }

  async clearAllMemories() {
    return this.client.delete('/v1/memories');
  }

  async getMemoryContext() {
    return this.client.get<{ context: string; has_memories: boolean }>('/v1/memories/context');
  }

  async getRuntimeSnapshot(threadId?: string) {
    return this.client.get<RuntimeSnapshotResponse>('/v1/memories/runtime-snapshot', {
      params: threadId ? { thread_id: threadId } : undefined,
    });
  }
}
